import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowRight, ArrowLeft, Check, Home, Briefcase, Wifi, Phone as PhoneIcon,
  User, Mail, MapPin, Building2, AlertCircle, Loader2, CheckCircle2,
  Zap, Plus, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Irish broadband providers for switching dropdown
const IRISH_PROVIDERS = [
  "Eir", "Virgin Media", "Sky Ireland", "Vodafone", "Pure Telecom",
  "Digiweb", "Imagine", "Blacknight", "Fastway", "Other"
];

// Irish Counties
const IRISH_COUNTIES = [
  "Antrim", "Armagh", "Carlow", "Cavan", "Clare", "Cork", "Derry", "Donegal",
  "Down", "Dublin", "Fermanagh", "Galway", "Kerry", "Kildare", "Kilkenny",
  "Laois", "Leitrim", "Limerick", "Longford", "Louth", "Mayo", "Meath",
  "Monaghan", "Offaly", "Roscommon", "Sligo", "Tipperary", "Tyrone",
  "Waterford", "Westmeath", "Wexford", "Wicklow"
];

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps, steps }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, i) => (
      <React.Fragment key={i}>
        <div className="flex flex-col items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            i < currentStep 
              ? 'bg-green-500 text-white' 
              : i === currentStep 
                ? 'bg-[#1E3064] text-white ring-4 ring-[#1E3064]/20' 
                : 'bg-slate-200 text-slate-500'
          }`}>
            {i < currentStep ? <Check className="w-5 h-5" /> : i + 1}
          </div>
          <span className={`text-xs mt-2 font-medium ${
            i <= currentStep ? 'text-[#1E3064]' : 'text-slate-400'
          }`}>{step}</span>
        </div>
        {i < totalSteps - 1 && (
          <div className={`w-12 md:w-24 h-1 mx-2 rounded ${
            i < currentStep ? 'bg-green-500' : 'bg-slate-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Input component with label
const FormInput = ({ label, error, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input
      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none transition ${
        error ? 'border-red-500 bg-red-50' : 'border-slate-300'
      }`}
      {...props}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Select component with label
const FormSelect = ({ label, options, error, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <select
      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none transition ${
        error ? 'border-red-500 bg-red-50' : 'border-slate-300'
      }`}
      {...props}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const OrderForm = ({ 
  availabilityResult, 
  eircode, 
  onClose, 
  preSelectedPackage = null,
  serviceType: initialServiceType = 'home'
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [errors, setErrors] = useState({});

  const steps = ['Service Type', 'Your Details', 'Select Package', 'Review & Order'];

  // Form data
  const [formData, setFormData] = useState({
    // Service type
    serviceType: initialServiceType,
    
    // Customer details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Business info (if business)
    businessName: '',
    tradingName: '',
    companyRegistration: '',
    vatNumber: '',
    businessType: '',
    
    // Installation address
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    eircode: eircode || '',
    
    // Switching info
    isSwitching: false,
    currentProvider: '',
    uan: '',
    
    // Package selection
    selectedPackageId: preSelectedPackage?.id || '',
    
    // Addons
    speedboost: false,
    voipLandline: false,
    
    // Network provider from availability
    networkProvider: '',
    
    // Notes
    customerNotes: ''
  });

  // Extract available product IDs from availability result
  const getAvailableProductIds = useCallback(() => {
    if (!availabilityResult) return [];
    
    const productIds = new Set();
    const providers = [availabilityResult.openeir, availabilityResult.siro, availabilityResult.nbi];
    
    providers.forEach(provider => {
      if (provider?.available && provider.products) {
        provider.products.forEach(product => {
          if (product.id !== null && product.id !== undefined) {
            productIds.add(product.id);
          }
        });
      }
    });
    
    return [...productIds];
  }, [availabilityResult]);

  // Fetch packages - filter by available product IDs if we have availability data
  const fetchPackages = useCallback(async () => {
    try {
      const availableIds = getAvailableProductIds();
      
      let url;
      if (availableIds.length > 0) {
        // Use the by-ids endpoint to only get packages matching available products
        url = `${API_URL}/api/public/packages/by-ids?ids=${availableIds.join(',')}&package_type=${formData.serviceType}`;
      } else {
        // Fallback to all packages if no availability data
        url = `${API_URL}/api/public/packages?package_type=${formData.serviceType}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        let data = await res.json();
        
        // Deduplicate packages with same download AND upload speed - keep cheapest
        const speedMap = new Map();
        data.forEach(pkg => {
          const speedKey = `${pkg.download_speed_mbps}-${pkg.upload_speed_mbps}`;
          const totalCost = (pkg.monthly_price || 0) + (pkg.setup_fee || 0) + (pkg.equipment_fee || 0);
          
          if (!speedMap.has(speedKey)) {
            speedMap.set(speedKey, { pkg, totalCost });
          } else {
            const existing = speedMap.get(speedKey);
            if (totalCost < existing.totalCost) {
              speedMap.set(speedKey, { pkg, totalCost });
            }
          }
        });
        
        // Convert back to array and sort by download speed
        const deduplicated = Array.from(speedMap.values())
          .map(item => item.pkg)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.download_speed_mbps - b.download_speed_mbps);
        
        setPackages(deduplicated);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  }, [formData.serviceType, getAvailableProductIds]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);
  
  // Helper to get the correct display name based on service type
  const getPackageDisplayName = (pkg) => {
    if (formData.serviceType === 'business' && pkg.business_display_name) {
      return pkg.business_display_name;
    }
    return pkg.display_name || pkg.name;
  };

  // Update form data
  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate current step
  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      // Customer details validation
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      
      if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.county.trim()) newErrors.county = 'County is required';
      if (!formData.eircode.trim()) newErrors.eircode = 'Eircode is required';
      
      if (formData.serviceType === 'business') {
        if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
      }
      
      if (formData.isSwitching && !formData.currentProvider) {
        newErrors.currentProvider = 'Please select your current provider';
      }
    }

    if (currentStep === 2) {
      if (!formData.selectedPackageId) newErrors.selectedPackageId = 'Please select a package';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  // Handle previous step
  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Submit order
  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    try {
      const orderData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.serviceType,
        installation_address: {
          address_line1: formData.addressLine1,
          address_line2: formData.addressLine2 || null,
          city: formData.city,
          county: formData.county,
          eircode: formData.eircode.toUpperCase()
        },
        package_id: formData.selectedPackageId,
        addons: {
          speedboost: formData.speedboost,
          voip_landline: formData.voipLandline
        },
        network_provider: formData.networkProvider || null,
        customer_notes: formData.customerNotes || null
      };

      // Add business info if business
      if (formData.serviceType === 'business') {
        orderData.business_info = {
          business_name: formData.businessName,
          trading_name: formData.tradingName || null,
          company_registration: formData.companyRegistration || null,
          vat_number: formData.vatNumber || null,
          business_type: formData.businessType || null
        };
      }

      // Add switching info
      if (formData.isSwitching) {
        orderData.switching_info = {
          is_switching: true,
          current_provider: formData.currentProvider,
          uan: formData.uan || null
        };
      }

      const res = await fetch(`${API_URL}/api/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      if (!res.ok) {
        throw new Error('Failed to place order');
      }

      const result = await res.json();
      setOrderResult(result);
      setOrderComplete(true);
    } catch (error) {
      console.error('Order error:', error);
      setErrors({ submit: 'Failed to place order. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Get selected package details
  const selectedPackage = packages.find(p => p.id === formData.selectedPackageId);

  // Calculate totals
  const calculateTotals = () => {
    if (!selectedPackage) return { monthly: 0, oneTime: 0 };
    
    let monthly = selectedPackage.monthly_price || 0;
    if (formData.speedboost && selectedPackage.speedboost_available) {
      monthly += selectedPackage.speedboost_price || 0;
    }
    if (formData.voipLandline && formData.serviceType === 'home') {
      monthly += selectedPackage.voip_price || 9.99;
    }
    
    // One-time fees: setup_fee (installation) + equipment_fee
    const oneTime = (selectedPackage.setup_fee || 0) + (selectedPackage.equipment_fee || 0);
    
    return { monthly, oneTime };
  };

  const totals = calculateTotals();

  // Order complete screen
  if (orderComplete && orderResult) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#1E3064] mb-2">Order Placed Successfully!</h2>
          <p className="text-slate-600 mb-4">Your order number is:</p>
          <p className="text-3xl font-mono font-bold text-[#F97530] mb-6">{orderResult.order_number}</p>
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold text-slate-900 mb-2">What happens next?</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Check your email for order confirmation and portal access
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Our team will review your order and contact you within 24 hours
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                You'll receive a service agreement to sign electronically
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Installation date will be scheduled at your convenience
              </li>
            </ul>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            Non-recurring fees (if any) will be collected upon successful installation along with the first month's prorated recurring fees.
          </p>
          <button
            onClick={onClose}
            className="w-full py-3 bg-[#1E3064] text-white rounded-lg font-semibold hover:bg-[#2a4080] transition"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-[#1E3064]">Order Broadband</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl">&times;</button>
          </div>
          <StepIndicator currentStep={currentStep} totalSteps={steps.length} steps={steps} />
        </div>

        {/* Form Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Step 0: Service Type */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Select Service Type</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => updateForm('serviceType', 'home')}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    formData.serviceType === 'home'
                      ? 'border-[#1E3064] bg-[#1E3064]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Home className={`w-10 h-10 mb-3 ${formData.serviceType === 'home' ? 'text-[#1E3064]' : 'text-slate-400'}`} />
                  <h4 className="text-lg font-semibold text-slate-900">Home Broadband</h4>
                  <p className="text-sm text-slate-500 mt-1">For residential properties</p>
                </button>
                <button
                  onClick={() => updateForm('serviceType', 'business')}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    formData.serviceType === 'business'
                      ? 'border-[#1E3064] bg-[#1E3064]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Briefcase className={`w-10 h-10 mb-3 ${formData.serviceType === 'business' ? 'text-[#1E3064]' : 'text-slate-400'}`} />
                  <h4 className="text-lg font-semibold text-slate-900">Business Broadband</h4>
                  <p className="text-sm text-slate-500 mt-1">For business premises</p>
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Your Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Your Details</h3>
              
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="First Name *"
                  value={formData.firstName}
                  onChange={(e) => updateForm('firstName', e.target.value)}
                  error={errors.firstName}
                  placeholder="John"
                />
                <FormInput
                  label="Last Name *"
                  value={formData.lastName}
                  onChange={(e) => updateForm('lastName', e.target.value)}
                  error={errors.lastName}
                  placeholder="Murphy"
                />
                <FormInput
                  label="Email Address *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateForm('email', e.target.value)}
                  error={errors.email}
                  placeholder="john@example.com"
                />
                <FormInput
                  label="Phone Number *"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  error={errors.phone}
                  placeholder="087 123 4567"
                />
              </div>

              {/* Business Info (if business) */}
              {formData.serviceType === 'business' && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Business Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Business Name *"
                      value={formData.businessName}
                      onChange={(e) => updateForm('businessName', e.target.value)}
                      error={errors.businessName}
                      placeholder="Acme Ltd"
                    />
                    <FormInput
                      label="Trading Name"
                      value={formData.tradingName}
                      onChange={(e) => updateForm('tradingName', e.target.value)}
                      placeholder="Optional"
                    />
                    <FormInput
                      label="Company Registration Number"
                      value={formData.companyRegistration}
                      onChange={(e) => updateForm('companyRegistration', e.target.value)}
                      placeholder="Optional"
                    />
                    <FormInput
                      label="VAT Number"
                      value={formData.vatNumber}
                      onChange={(e) => updateForm('vatNumber', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              )}

              {/* Installation Address */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Installation Address
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <FormInput
                      label="Address Line 1 *"
                      value={formData.addressLine1}
                      onChange={(e) => updateForm('addressLine1', e.target.value)}
                      error={errors.addressLine1}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <FormInput
                      label="Address Line 2"
                      value={formData.addressLine2}
                      onChange={(e) => updateForm('addressLine2', e.target.value)}
                      placeholder="Apartment, suite, etc. (optional)"
                    />
                  </div>
                  <FormInput
                    label="City/Town *"
                    value={formData.city}
                    onChange={(e) => updateForm('city', e.target.value)}
                    error={errors.city}
                    placeholder="Dublin"
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">County *</label>
                    <select
                      value={formData.county}
                      onChange={(e) => updateForm('county', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none transition bg-white ${
                        errors.county ? 'border-red-500' : 'border-slate-300'
                      }`}
                      data-testid="county-select"
                    >
                      <option value="">Select County</option>
                      {IRISH_COUNTIES.map((county) => (
                        <option key={county} value={county}>{county}</option>
                      ))}
                    </select>
                    {errors.county && <p className="text-red-500 text-sm mt-1">{errors.county}</p>}
                  </div>
                  <FormInput
                    label="Eircode *"
                    value={formData.eircode}
                    onChange={(e) => updateForm('eircode', e.target.value.toUpperCase())}
                    error={errors.eircode}
                    placeholder="D02X285"
                    maxLength={7}
                  />
                </div>
              </div>

              {/* Switching Provider */}
              <div className="bg-blue-50 rounded-xl p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="isSwitching"
                    checked={formData.isSwitching}
                    onChange={(e) => updateForm('isSwitching', e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-slate-300 text-[#1E3064] focus:ring-[#1E3064]"
                  />
                  <label htmlFor="isSwitching" className="text-slate-700">
                    I'm switching from another fibre broadband provider
                  </label>
                </div>

                {formData.isSwitching && (
                  <div className="space-y-4 pl-8">
                    <FormSelect
                      label="Current Provider *"
                      value={formData.currentProvider}
                      onChange={(e) => updateForm('currentProvider', e.target.value)}
                      error={errors.currentProvider}
                      options={[
                        { value: '', label: 'Select provider...' },
                        ...IRISH_PROVIDERS.map(p => ({ value: p, label: p }))
                      ]}
                    />
                    <div>
                      <FormInput
                        label="UAN (Unique Access Number)"
                        value={formData.uan}
                        onChange={(e) => updateForm('uan', e.target.value)}
                        placeholder="e.g., 1234567890"
                      />
                      <div className="mt-2 p-3 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-slate-600">
                            <p className="font-medium text-blue-700 mb-1">Where to find your UAN:</p>
                            <ul className="list-disc list-inside space-y-1 text-xs">
                              <li>Check your current provider's bill or account page</li>
                              <li>Contact your current provider's customer service</li>
                              <li>It's usually a 10-digit number</li>
                            </ul>
                            <p className="mt-2 text-xs">
                              The UAN allows us to switch your service smoothly without downtime.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select Package */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                Select Your Package
                {availabilityResult && (
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    (Based on availability at {eircode})
                  </span>
                )}
              </h3>

              {errors.selectedPackageId && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {errors.selectedPackageId}
                </div>
              )}

              {packages.length === 0 && availabilityResult && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                  <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">No Matching Packages Available</h4>
                  <p className="text-sm text-slate-600">
                    We don't have any configured packages matching the services available at your address yet. 
                    Please contact us at <a href="tel:+353656712772" className="text-[#1E3064] font-medium">065 671 2772</a> and 
                    we'll help you find the right plan.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => updateForm('selectedPackageId', pkg.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left relative ${
                      formData.selectedPackageId === pkg.id
                        ? 'border-[#1E3064] bg-[#1E3064]/5 ring-2 ring-[#1E3064]/20'
                        : pkg.is_most_popular
                          ? 'border-[#F97530] bg-[#F97530]/5'
                          : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {/* Most Popular Badge */}
                    {pkg.is_most_popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white text-xs font-bold px-3 py-1 rounded-full">
                        Most Popular
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-900">{getPackageDisplayName(pkg)}</h4>
                        {/* SpeedBoost Lightning Bolt */}
                        {pkg.includes_speedboost && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium" title="Includes SpeedBoost">
                            <Zap className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      {formData.selectedPackageId === pkg.id && (
                        <Check className="w-5 h-5 text-[#1E3064]" />
                      )}
                    </div>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-2xl font-bold text-[#1E3064]">
                        {pkg.download_speed_mbps >= 1000 
                          ? `${pkg.download_speed_mbps / 1000}Gb` 
                          : `${pkg.download_speed_mbps}Mb`}
                      </span>
                      <span className="text-slate-500 text-sm">download</span>
                    </div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-lg font-semibold text-[#1E3064]/70">
                        {pkg.upload_speed_mbps >= 1000 
                          ? `${pkg.upload_speed_mbps / 1000}Gb` 
                          : `${pkg.upload_speed_mbps}Mb`}
                      </span>
                      <span className="text-slate-400 text-sm">upload</span>
                    </div>
                    <div className="text-lg font-semibold text-[#F97530]">
                      €{pkg.monthly_price?.toFixed(2)}/month
                      <span className="text-xs text-slate-400 font-normal block">excl. VAT</span>
                    </div>
                    {(pkg.setup_fee > 0 || pkg.equipment_fee > 0) && (
                      <div className="text-xs text-slate-500 mt-1">
                        {pkg.setup_fee > 0 && <span>Setup: €{pkg.setup_fee?.toFixed(2)} </span>}
                        {pkg.equipment_fee > 0 && <span>Equipment: €{pkg.equipment_fee?.toFixed(2)}</span>}
                      </div>
                    )}
                    {pkg.contract_length_months === 0 && (
                      <span className="text-xs text-green-600 font-medium">No contract</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Addons */}
              {selectedPackage && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-slate-900">Add-ons</h4>
                  
                  {/* SpeedBoost */}
                  {selectedPackage.speedboost_available && (
                    <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-[#1E3064]/30">
                      <input
                        type="checkbox"
                        checked={formData.speedboost}
                        onChange={(e) => updateForm('speedboost', e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-slate-300 text-[#1E3064] focus:ring-[#1E3064]"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-purple-500" />
                          <span className="font-medium text-slate-900">SpeedBoost</span>
                          <span className="text-[#F97530] font-semibold">+€{selectedPackage.speedboost_price}/month</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Upgrade to {selectedPackage.speedboost_speed_mbps}Mb download speed
                        </p>
                      </div>
                    </label>
                  )}

                  {/* VOIP Landline (home only) */}
                  {formData.serviceType === 'home' && selectedPackage.voip_available && (
                    <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-[#1E3064]/30">
                      <input
                        type="checkbox"
                        checked={formData.voipLandline}
                        onChange={(e) => updateForm('voipLandline', e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-slate-300 text-[#1E3064] focus:ring-[#1E3064]"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <PhoneIcon className="w-5 h-5 text-green-500" />
                          <span className="font-medium text-slate-900">VOIP Landline</span>
                          <span className="text-[#F97530] font-semibold">+€{selectedPackage.voip_price || 9.99}/month</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Add a home phone line to your broadband service
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review & Order */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Review Your Order</h3>

              {errors.submit && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {errors.submit}
                </div>
              )}

              {/* Customer Details Summary */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-3">Customer Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Name:</span>
                    <span className="ml-2 text-slate-900">{formData.firstName} {formData.lastName}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Email:</span>
                    <span className="ml-2 text-slate-900">{formData.email}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Phone:</span>
                    <span className="ml-2 text-slate-900">{formData.phone}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Service Type:</span>
                    <span className="ml-2 text-slate-900 capitalize">{formData.serviceType}</span>
                  </div>
                </div>
                {formData.serviceType === 'business' && formData.businessName && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <span className="text-slate-500">Business:</span>
                    <span className="ml-2 text-slate-900">{formData.businessName}</span>
                  </div>
                )}
              </div>

              {/* Installation Address Summary */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-3">Installation Address</h4>
                <p className="text-slate-700">
                  {formData.addressLine1}
                  {formData.addressLine2 && <>, {formData.addressLine2}</>}
                  <br />
                  {formData.city}, {formData.county}
                  <br />
                  {formData.eircode}
                </p>
              </div>

              {/* Switching Info */}
              {formData.isSwitching && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Switching From</h4>
                  <p className="text-slate-700">
                    {formData.currentProvider}
                    {formData.uan && <span className="ml-2 text-slate-500">(UAN: {formData.uan})</span>}
                  </p>
                </div>
              )}

              {/* Package Summary */}
              {selectedPackage && (
                <div className="bg-[#1E3064]/5 rounded-xl p-4">
                  <h4 className="font-semibold text-[#1E3064] mb-3">Selected Package</h4>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h5 className="font-semibold text-slate-900">{selectedPackage.display_name || selectedPackage.name}</h5>
                      <p className="text-sm text-slate-600">
                        {selectedPackage.download_speed_mbps >= 1000 
                          ? `${selectedPackage.download_speed_mbps / 1000}Gb` 
                          : `${selectedPackage.download_speed_mbps}Mb`} download / 
                        {selectedPackage.upload_speed_mbps >= 1000 
                          ? `${selectedPackage.upload_speed_mbps / 1000}Gb` 
                          : `${selectedPackage.upload_speed_mbps}Mb`} upload
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-[#F97530]">€{selectedPackage.monthly_price}</span>
                      <span className="text-slate-500">/month</span>
                    </div>
                  </div>

                  {/* Addons */}
                  {(formData.speedboost || formData.voipLandline) && (
                    <div className="border-t border-slate-200 pt-3 space-y-2">
                      {formData.speedboost && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">SpeedBoost</span>
                          <span className="text-slate-900">+€{selectedPackage.speedboost_price}/month</span>
                        </div>
                      )}
                      {formData.voipLandline && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">VOIP Landline</span>
                          <span className="text-slate-900">+€{selectedPackage.voip_price || 9.99}/month</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Totals */}
                  <div className="border-t border-slate-200 mt-4 pt-4">
                    <div className="flex justify-between font-semibold">
                      <span className="text-slate-900">Monthly Total:</span>
                      <span className="text-[#1E3064] text-lg">€{totals.monthly.toFixed(2)}/month</span>
                    </div>
                    <p className="text-xs text-slate-400 text-right">excl. VAT</p>
                    
                    {/* One-time fees breakdown */}
                    {totals.oneTime > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-sm font-medium text-slate-700 mb-2">One-time Fees:</p>
                        {selectedPackage.setup_fee > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Installation/Setup:</span>
                            <span className="text-slate-700">€{selectedPackage.setup_fee.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedPackage.equipment_fee > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Equipment:</span>
                            <span className="text-slate-700">€{selectedPackage.equipment_fee.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-dashed border-slate-200">
                          <span className="text-slate-700">Total One-time:</span>
                          <span className="text-slate-900">€{totals.oneTime.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Notice */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">No payment required today</p>
                    <p className="mt-1">Non-recurring fees (if any) will be collected upon successful installation along with the first month's prorated recurring fees.</p>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Additional Notes (optional)</label>
                <textarea
                  value={formData.customerNotes}
                  onChange={(e) => updateForm('customerNotes', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none resize-none"
                  rows={3}
                  placeholder="Any special instructions or notes for our team..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer with navigation */}
        <div className="p-6 border-t border-slate-200 flex items-center justify-between">
          <button
            onClick={currentStep === 0 ? onClose : handlePrev}
            className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </button>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-[#1E3064] text-white rounded-lg font-semibold hover:bg-[#2a4080] transition flex items-center gap-2"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Placing Order...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Place Order
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
